import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Wand2, Users } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import LlmRegenerateButton from "./LlmRegenerateButton";
import SeoMetaEditor from "./SeoMetaEditor";
import ViewLiveLink from "./ViewLiveLink";

const CrudSection = ({ title, endpoint, writeEndpoint, fields, generatePrompt, filterField }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const writeUrl = writeEndpoint || endpoint;

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error(`Failed to fetch ${endpoint}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter items client-side based on active filter
  const filteredItems = activeFilter === "all" 
    ? items 
    : items.filter(item => item[filterField?.name] === activeFilter);

  // Get filter options and counts from the filterField
  const filterOptions = filterField ? filterField.options || [] : [];
  const filterCounts = filterField ? filterOptions.reduce((acc, opt) => {
    acc[opt] = items.filter(item => item[filterField.name] === opt).length;
    return acc;
  }, {}) : {};

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/${writeUrl}/${editingItem.id || editingItem.year}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Updated successfully!");
      } else {
        await axios.post(`${API}/${writeUrl}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Created successfully!");
      }
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!confirm("Are you sure you want to delete this item?")) return;
    
    try {
      await axios.delete(`${API}/${writeUrl}/${item.id || item.year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully!");
      fetchItems();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleGenerate = async () => {
    if (!generatePrompt) return;
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-content`, generatePrompt(formData), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the content field
      const contentField = fields.find(f => f.name === 'content' || f.name === 'biography' || f.name === 'summary');
      if (contentField) {
        setFormData({ ...formData, [contentField.name]: response.data.content });
      }
      toast.success("Content generated!");
    } catch (error) {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const openDialog = (item = null) => {
    setEditingItem(item);
    setFormData(item ? { ...item } : {});
    setDialogOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h1>
          {filterField && activeFilter !== "all" && (
            <span className="text-sm text-slate-500">
              Showing {filteredItems.length} of {items.length}
            </span>
          )}
        </div>
        <Button onClick={() => openDialog()} className="bg-[#7C2C3B] hover:bg-[#63232F]">
          <Plus className="w-4 h-4 mr-2" /> ADD NEW
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          No items yet. Click "Add New" to create one.
        </div>
      ) : (
        <>
          {/* Category Filter Bar */}
          {filterField && filterOptions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="category-filter-bar">
              <button
                onClick={() => setActiveFilter("all")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  activeFilter === "all" 
                    ? "bg-[#7C2C3B] text-white" 
                    : "bg-white border border-slate-200 text-slate-600 hover:border-[#7C2C3B]"
                }`}
                data-testid="filter-all"
              >
                All ({items.length})
              </button>
              {filterOptions.map(opt => (
                <button
                  key={opt}
                  onClick={() => setActiveFilter(opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                    activeFilter === opt 
                      ? "bg-[#7C2C3B] text-white" 
                      : "bg-white border border-slate-200 text-slate-600 hover:border-[#7C2C3B]"
                  }`}
                  data-testid={`filter-${opt}`}
                >
                  {opt.replace('_', ' ')} ({filterCounts[opt] || 0})
                </button>
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {endpoint === "players" && <TableHead className="w-16">Photo</TableHead>}
                {fields.slice(0, 4).map(field => (
                  <TableHead key={field.name}>{field.label}</TableHead>
                ))}
                {endpoint === "players" && <TableHead>Social</TableHead>}
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <TableRow key={item.id || idx}>
                  {endpoint === "players" && (
                    <TableCell>
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name} 
                          className="w-12 h-12 rounded-full object-cover bg-slate-100"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                          <Users className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                    </TableCell>
                  )}
                  {fields.slice(0, 4).map(field => (
                    <TableCell key={field.name} className="max-w-xs truncate">
                      {field.type === "checkbox" 
                        ? (item[field.name] ? "✓" : "-")
                        : Array.isArray(item[field.name]) 
                          ? item[field.name].join(", ") 
                          : String(item[field.name] || "-").substring(0, 50)}
                    </TableCell>
                  ))}
                  {endpoint === "players" && (
                    <TableCell>
                      <div className="flex gap-1">
                        {item.instagram && (
                          <span className="w-6 h-6 bg-gradient-to-tr from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center" title={`@${item.instagram}`}>
                            <span className="text-white text-xs">IG</span>
                          </span>
                        )}
                        {item.tiktok && (
                          <span className="w-6 h-6 bg-black rounded-full flex items-center justify-center" title={`@${item.tiktok}`}>
                            <span className="text-white text-xs">TT</span>
                          </span>
                        )}
                        {!item.instagram && !item.tiktok && (
                          <span className="text-slate-300 text-xs">None</span>
                        )}
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex gap-2">
                      {/* CrudSection is used for chants, history and other lists.
                          Only chants have a public detail page currently. */}
                      {endpoint === "chants" && (
                        <ViewLiveLink slug={item.slug} path="/chants" testId={`chant-${item.id}`} />
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openDialog(item)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(item)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingItem ? "EDIT ITEM" : "ADD NEW ITEM"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Image preview for players */}
            {endpoint === "players" && (
              <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                <div className="relative">
                  {formData.image_url ? (
                    <img 
                      src={formData.image_url} 
                      alt="Player preview" 
                      className="w-24 h-24 rounded-full object-cover bg-slate-100 border-2 border-slate-200"
                      onError={(e) => { 
                        e.target.src = ''; 
                        e.target.className = 'w-24 h-24 rounded-full bg-red-100 border-2 border-red-300';
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center border-2 border-slate-300">
                      <Users className="w-10 h-10 text-slate-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">{formData.name || "Player Name"}</h3>
                  <p className="text-sm text-slate-500">
                    {formData.image_url ? "Photo URL set" : "No photo - add URL below"}
                  </p>
                  {formData.image_url && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 mt-1 h-7 px-2"
                      onClick={() => setFormData({ ...formData, image_url: "" })}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Remove Photo
                    </Button>
                  )}
                </div>
              </div>
            )}

            {fields.map(field => (
              <div key={field.name}>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">
                    {field.label}
                    {field.name === "image_url" && endpoint === "players" && (
                      <span className="text-xs text-slate-400 ml-2">(paste URL from social media or official source)</span>
                    )}
                  </label>
                  {field.aiRegenerate && editingItem?.id && (
                    <LlmRegenerateButton
                      collection={field.aiRegenerate}
                      docId={editingItem.id}
                      onText={(text) => setFormData({ ...formData, [field.name]: text })}
                      testId={`crud-regenerate-${field.name}`}
                    />
                  )}
                </div>
                {field.type === "textarea" ? (
                  <Textarea
                    value={formData[field.name] || ""}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    rows={6}
                    className="mt-1"
                    placeholder={field.placeholder}
                  />
                ) : field.type === "select" ? (
                  <Select 
                    value={formData[field.name] || ""} 
                    onValueChange={(val) => setFormData({ ...formData, [field.name]: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={`Select ${field.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.type === "array" ? (
                  <Input
                    value={(formData[field.name] || []).join(", ")}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      [field.name]: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder={field.placeholder || "Comma-separated values"}
                    className="mt-1"
                  />
                ) : field.type === "checkbox" ? (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id={field.name}
                      checked={formData[field.name] || false}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-[#7C2C3B] focus:ring-[#7C2C3B]"
                    />
                    <label htmlFor={field.name} className="text-sm text-slate-600">
                      {field.placeholder || `Mark as ${field.label}`}
                    </label>
                  </div>
                ) : (
                  <Input
                    type={field.type || "text"}
                    value={formData[field.name] || ""}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    placeholder={field.placeholder}
                    className="mt-1"
                  />
                )}
              </div>
            ))}

            <div className="flex gap-2 pt-4">
              {generatePrompt && (
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="border-[#2BAEE5] text-[#2BAEE5]"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  AI GENERATE
                </Button>
              )}
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-[#7C2C3B] hover:bg-[#63232F]"
              >
                {submitting ? "SAVING..." : "SAVE"}
              </Button>
            </div>

            {/* SEO metadata editor — only when editing an existing doc
                AND the resource is one of the SEO-indexed collections.
                CrudSection is used for chants, history and other
                content types; chants is the SEO-relevant one here. */}
            {editingItem?.id && endpoint === "chants" && (
              <SeoMetaEditor collection="chants" docId={editingItem.id} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrudSection;
